// recaptcha.service.ts
import { inject, Injectable, NgZone } from '@angular/core';
import { ENVIRONMENT, Environment } from '../config/environment-config';
import { RecaptchaAction } from '../enum/recaptcha-action.enum';

declare const grecaptcha: any;

@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private scriptLoadingPromise: Promise<void> | null = null;
  environment = inject<Environment>(ENVIRONMENT);
  zone = inject(NgZone);

  constructor() {
    this.loadScript();
  }

  private loadScript(): Promise<void> {
    if (this.scriptLoadingPromise) return this.scriptLoadingPromise;

    this.scriptLoadingPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        'script[src*="https://www.google.com/recaptcha/api.js"]',
      );
      if (existing) {
        this.waitForGrecaptchaReady().then(resolve).catch(reject);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.environment.RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.waitForGrecaptchaReady().then(resolve).catch(reject);
      };
      script.onerror = () => reject('Failed to load reCAPTCHA script');

      document.body.appendChild(script);
    });

    return this.scriptLoadingPromise;
  }

  async execute(action: RecaptchaAction): Promise<string> {
    await this.loadScript();

    return this.zone.runOutsideAngular(
      () =>
        new Promise<string>((resolve, reject) => {
          grecaptcha.ready(() => {
            grecaptcha
              .execute(this.environment.RECAPTCHA_SITE_KEY, {
                action: action.toString(),
              })
              .then(resolve)
              .catch(reject);
          });
        }),
    );
  }

  private waitForGrecaptchaReady(): Promise<void> {
    return new Promise((resolve, reject) => {
      const maxAttempts = 100;
      let attempts = 0;

      const checkReady = () => {
        if (typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
          try {
            grecaptcha.ready(() => {
              resolve();
            });
          } catch (err) {
            if (attempts >= maxAttempts) {
              reject('reCAPTCHA failed to initialize: ' + err);
            } else {
              attempts++;
              setTimeout(checkReady, 100);
            }
          }
        } else if (attempts >= maxAttempts) {
          reject('reCAPTCHA script loaded but grecaptcha object not found');
        } else {
          attempts++;
          setTimeout(checkReady, 100);
        }
      };

      checkReady();
    });
  }
}
